import { useState } from 'react';
import { Pencil, RotateCcw, Loader2 } from 'lucide-react';
import adminAxios from '../lib/adminAxios';
import useProducts, { invalidateProducts } from '../hooks/useProducts';
import EditSetDialog from './EditSetDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lists all 4 sets with status (Original / Edited) and Edit / Reset buttons.

export const EditSetsPanel = ({ onChanged }) => {
  const { sets, hasSetOverride, refetch } = useProducts();
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState({});

  const onSaved = async () => {
    invalidateProducts();
    await refetch();
    onChanged?.();
  };

  const reset = async (slug) => {
    if (!confirm(`Reset this set to its original house data?`)) return;
    setBusy((b) => ({ ...b, [slug]: true }));
    try {
      await adminAxios.delete(`${API}/admin/sets/${encodeURIComponent(slug)}`);
      onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy((b) => ({ ...b, [slug]: false }));
    }
  };

  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24" data-testid="admin-edit-sets-panel">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="eyebrow text-gold mb-4">The four sets</div>
          <h2 className="font-display text-3xl md:text-4xl text-mb">Edit any set.</h2>
        </div>
        <div className="eyebrow-sm text-mb-muted hidden md:block">
          Change name, tier, tagline, description, leather, packaging, pieces & order
        </div>
      </div>

      <div className="hairline border-subtle bg-mb-surface">
        <div className="grid grid-cols-[80px_minmax(160px,1fr)_minmax(120px,1fr)_minmax(80px,80px)_minmax(120px,1fr)_auto] gap-3 items-center px-4 py-3 hairline-b border-subtle eyebrow-sm text-mb-muted hidden md:grid">
          <div>Tier</div>
          <div>Name</div>
          <div>Tagline</div>
          <div>Pieces</div>
          <div>Status</div>
          <div className="text-right">Actions</div>
        </div>

        {sets.map((s) => {
          const overridden = hasSetOverride(s.slug);
          return (
            <div
              key={s.slug}
              className="grid grid-cols-[80px_minmax(160px,1fr)_minmax(120px,1fr)_minmax(80px,80px)_minmax(120px,1fr)_auto] gap-3 items-center px-4 py-3 hairline-b border-subtle hover:bg-mb-rich/30 transition-colors"
              data-testid={`admin-edit-set-row-${s.slug}`}
            >
              <div className="eyebrow-sm text-gold whitespace-nowrap">{s.tier}</div>
              <div>
                <div className="font-display text-[15px] text-mb">{s.name}</div>
                {s.featured && <div className="eyebrow-sm text-gold mt-0.5">Flagship ●</div>}
              </div>
              <div className="eyebrow-sm text-mb-muted truncate italic">{s.tagline || '—'}</div>
              <div className="eyebrow-sm text-mb">{s.items.length}</div>
              <div className={`eyebrow-sm ${overridden ? 'text-gold' : 'text-mb-muted'}`}>
                {overridden ? 'Edited' : 'Original'}
              </div>
              <div className="flex items-center gap-1 justify-end">
                <button
                  className="text-mb hover:text-gold p-2"
                  onClick={() => setEditing(s)}
                  title="Edit set"
                  data-testid={`admin-edit-set-${s.slug}`}
                >
                  <Pencil size={14} strokeWidth={1.25} />
                </button>
                {overridden && (
                  <button
                    className="text-mb hover:text-gold p-2 disabled:opacity-30"
                    onClick={() => reset(s.slug)}
                    disabled={busy[s.slug]}
                    title="Reset to original"
                    data-testid={`admin-edit-set-reset-${s.slug}`}
                  >
                    {busy[s.slug] ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} strokeWidth={1.25} />}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <EditSetDialog
          set={editing}
          hasSetOverride={hasSetOverride(editing.slug)}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </section>
  );
};

export default EditSetsPanel;
